import MomentUtils from '@date-io/moment';
import {
  Box,
  Button,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  NoSsr,
  ThemeProvider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import theme from 'components/theme/theme';
import { isDark } from 'components/utils/isDark';
import ls from 'components/utils/secureStorage';
import { navigate } from 'gatsby';
import { DialogProvider } from 'muibox';
import { SnackbarProvider } from 'notistack';
import { useEffect } from 'react';
import { useSetStoreValue, withStore } from 'react-context-hook';
import { RefProvider } from 'react-context-refs';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider as HTTPProvider } from 'use-http';
import useSession from './utils/useSession';
import { NotificationProvider } from './utils/NotificationContext';
import { AdminNotificationProvider } from './utils/AdminNotificationContext';

require('@openfonts/quicksand_latin');

const useSnackbarStyles = makeStyles({
  variantSuccess: {
    backgroundImage: `linear-gradient(to top, ${theme.palette.success.dark}, ${theme.palette.success.light})`,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  variantError: {
    backgroundImage: `linear-gradient(to top, ${theme.palette.error.dark}, ${theme.palette.error.light})`,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  variantWarning: {
    backgroundImage: `linear-gradient(to top, ${theme.palette.warning.dark}, ${theme.palette.warning.light})`,
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  variantInfo: {
    backgroundImage: `linear-gradient(to top, ${theme.palette.info.dark}, ${theme.palette.info.light})`,
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
});

const options = {
  loading: false,
  interceptors: {
    request: async ({ options, url, path, route }) => {
      const session = ls.get('session');

      if (session) {
        options.headers.Authorization = `Bearer ${session.token}`;
      }

      return options;
    },
    response: async ({ response }) => {
      if (response.status === 403) {
        const timestamp = Math.floor(new Date().getTime() / 1000);
        localStorage.setItem(
          'session.lastSync',
          JSON.stringify({
            clientId: null,
            timestamp,
          })
        );

        navigate('/login');
      }

      if (!response.ok) {
        throw new Error(
          JSON.stringify({
            ...response.data,
            code: response.status,
            status: response.statusText,
          })
        );
      }

      return response;
    },
  },
};

const DisplayError = ({ error, resetErrorBoundary }) => {
  const session = useSession();

  return (
    <Dialog open maxWidth="lg" fullWidth>
      <DialogTitle>Something went Wrong!</DialogTitle>
      <DialogContent>
        <Box maxHeight="75vh" overflow="auto">
          {JSON.stringify(error.message, null, 2)}
        </Box>
      </DialogContent>
      <DialogActions>
        <CopyToClipboard
          onCopy={() =>
            window.alert('Error copied, please send to developer with details!')
          }
          text={JSON.stringify({
            timestamp: new Date().toISOString(),
            url: window.location.href,
            session,
            error: error.message,
          })}>
          <Button>Copy to Clipboard</Button>
        </CopyToClipboard>
        <Button
          onClick={resetErrorBoundary}
          color="primary"
          variant="contained">
          Try again
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Root = withStore(
  ({ element }) => {
    const snackbarClasses = useSnackbarStyles();

    return (
      <NoSsr>
        <ErrorBoundary
          FallbackComponent={
            process.env.NODE_ENV !== 'development' ? DisplayError : () => null
          }
          onReset={() =>
            window.location.assign(`${process.env.GATSBY_BASE_URL || ''}/`)
          }>
          <RefProvider>
            <ThemeProvider theme={theme}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <DialogProvider>
                  <HTTPProvider options={options}>
                    {(() => {
                      let role = null;
                      if (typeof window !== 'undefined') {
                        try {
                          role = ls.get('role') || ls.get('session')?.role;
                        } catch (e) {
                          role = null;
                        }

                        if (!role) {
                          role = localStorage.getItem('role');
                        } else {
                          // do nothing
                        }
                      }
                      if (role === 'user') {
                        return (
                          <NotificationProvider>
                            <SnackbarProvider
                              anchorOrigin={{
                                horizontal: 'center',
                                vertical: 'bottom',
                              }}
                              classes={snackbarClasses}>
                              <CssBaseline />
                              {element}
                            </SnackbarProvider>
                          </NotificationProvider>
                        );
                      } else if (role === 'admin') {
                        return (
                          <AdminNotificationProvider>
                            <SnackbarProvider
                              anchorOrigin={{
                                horizontal: 'center',
                                vertical: 'bottom',
                              }}
                              classes={snackbarClasses}>
                              <CssBaseline />
                              {element}
                            </SnackbarProvider>
                          </AdminNotificationProvider>
                        );
                      }
                      return (
                        <SnackbarProvider
                          anchorOrigin={{
                            horizontal: 'center',
                            vertical: 'bottom',
                          }}
                          classes={snackbarClasses}>
                          <CssBaseline />
                          {element}
                        </SnackbarProvider>
                      );
                    })()}
                  </HTTPProvider>
                </DialogProvider>
              </MuiPickersUtilsProvider>
            </ThemeProvider>
          </RefProvider>
        </ErrorBoundary>
      </NoSsr>
    );
  },
  {
    'current-project': {
      loading: true,
      error: false,
      data: {},
    },
    'current-milestone': {
      loading: true,
      error: false,
      data: {},
    },
    'current-language': 'en',
  }
);

export default Root;
